if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.gallerySwiper = null;
        this._swiperWrapper = null;
        this._initialSlide = 0;
        if (!this.elements.thumbnails && this.dataset.desktopLayout !== 'slider') return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
        }
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        if (this.dataset.desktopLayout === 'slider') {
          this._initSwiperGallery();
        }
      }

      _checkSwiperGallery() {
        if (this.mql.matches) {
          this._initSwiperGallery();
        } else {
          this._destroySwiperGallery();
        }
      }

      _initSwiperGallery() {
        if (this.gallerySwiper || typeof Swiper !== 'function') return;
        const list = this.elements.viewer?.querySelector('[id^="Slider-Gallery"]');
        if (!list || !list.parentElement) return;

        // Liquid prepends the variant's featured_media to slide 0. Reorder
        // slides to match the natural product media order so the gallery is
        // consistent with swatch-click navigation (which skips DOM changes).
        this._reorderSlidesToMediaOrder(list);

        // Only create the wrapper if it doesn't already exist (first init)
        if (!this._swiperWrapper) {
          this._swiperWrapper = document.createElement('div');
          this._swiperWrapper.className = 'swiper product-gallery-swiper';
          list.parentElement.insertBefore(this._swiperWrapper, list);
          this._swiperWrapper.appendChild(list);
        }

        list.classList.add('swiper-wrapper');
        list.classList.remove('slider', 'slider--mobile', 'grid', 'grid--peek');
        list.querySelectorAll('[id^="Slide-"]').forEach((el) => el.classList.add('swiper-slide'));
        const totalSlides = list.querySelectorAll('.swiper-slide').length;
        if (totalSlides < 2) {
          list.classList.remove('swiper-wrapper');
          list.querySelectorAll('.swiper-slide').forEach((el) => el.classList.remove('swiper-slide'));
          if (this._swiperWrapper.parentElement) {
            this._swiperWrapper.parentElement.insertBefore(list, this._swiperWrapper);
            this._swiperWrapper.remove();
          }
          this._swiperWrapper = null;
          return;
        }
        const prevBtn = this.elements.viewer.querySelector('button[name="previous"]');
        const nextBtn = this.elements.viewer.querySelector('button[name="next"]');
        const currentEl = this.elements.viewer.querySelector('.slider-counter--current');
        const totalEl = this.elements.viewer.querySelector('.slider-counter--total');
        const startIndex = this._initialSlide || this._getInitialSlideForVariant(list) || 0;
        this._initialSlide = 0;

        this.gallerySwiper = new Swiper(this._swiperWrapper, {
          slidesPerView: 1,
          spaceBetween: 0,
          loop: false,
          speed: 500,
          initialSlide: startIndex,
          effect: 'creative',
          creativeEffect: {
            prev: { translate: ['-200%', 0, 0] },
            next: { translate: ['200%', 0, 0] },
          },
          simulateTouch: true,
          allowTouchMove: true,
          on: {
            init: (swiper) => {
              this._infoWrapper = this.closest('.product')?.querySelector('.product__info-wrapper');
              const slide = swiper.slides[swiper.activeIndex];
              if (slide) {
                slide.classList.add('is-active');
                this.playActiveMedia(slide);
                // Sync thumbnail to match initial slide
                if (this.elements.thumbnails) {
                  const activeThumbnail = this.elements.thumbnails.querySelector(
                    `[data-target="${slide.dataset.mediaId}"]`
                  );
                  this.setActiveThumbnail(activeThumbnail);
                }
              }
              if (currentEl) currentEl.textContent = swiper.activeIndex + 1;
              if (totalEl) totalEl.textContent = totalSlides;
              this._updateNavButtons(swiper, prevBtn, nextBtn);
            },
            touchStart: () => {
              this._infoWrapper?.classList.add('product__info-wrapper--blur');
            },
            slideChangeTransitionStart: () => {
              this._infoWrapper?.classList.add('product__info-wrapper--blur');
            },
            slideChangeTransitionEnd: () => {
              this._infoWrapper?.classList.remove('product__info-wrapper--blur');
            },
            transitionEnd: () => {
              /* Catches snap-back when drag doesn't change slide */
              this._infoWrapper?.classList.remove('product__info-wrapper--blur');
            },
            slideChange: (swiper) => {
              list.querySelectorAll('[data-media-id]').forEach((el) => el.classList.remove('is-active'));
              const slide = swiper.slides[swiper.activeIndex];
              if (slide) {
                slide.classList.add('is-active');
                this.playActiveMedia(slide);
                if (currentEl) currentEl.textContent = swiper.activeIndex + 1;
                this.elements.viewer.dispatchEvent(
                  new CustomEvent('slideChanged', {
                    detail: { currentPage: swiper.activeIndex + 1, currentElement: slide },
                  })
                );
              }
              this._updateNavButtons(swiper, prevBtn, nextBtn);
            },
          },
        });
        if (prevBtn) prevBtn.addEventListener('click', () => this.gallerySwiper?.slidePrev());
        if (nextBtn) nextBtn.addEventListener('click', () => this.gallerySwiper?.slideNext());
      }

      _destroySwiperGallery() {
        if (!this.gallerySwiper) return;
        this.gallerySwiper.destroy(true, true);
        this.gallerySwiper = null;
        const list = this._swiperWrapper?.querySelector('.swiper-wrapper');
        if (list) {
          list.classList.remove('swiper-wrapper');
          list.querySelectorAll('.swiper-slide').forEach((el) => el.classList.remove('swiper-slide'));
        }
        // Keep the wrapper element in the DOM — _initSwiperGallery reuses it.
      }

      _updateNavButtons(swiper, prevBtn, nextBtn) {
        if (prevBtn) prevBtn.disabled = swiper.isBeginning;
        if (nextBtn) nextBtn.disabled = swiper.isEnd;
      }

      /**
       * Reorder slide <li> elements to match the natural product media order
       * stored in data-media-order. Liquid prepends the variant's featured_media
       * to position 0, which creates a different order than swatch-click navigation.
       * This undoes that prepend so the gallery is consistent.
       */
      _reorderSlidesToMediaOrder(list) {
        const mediaOrder = this.dataset.mediaOrder;
        if (!mediaOrder) return;

        const sectionId = this.id.replace('MediaGallery-', '');
        const orderArr = mediaOrder.split(',');
        const slides = Array.from(list.querySelectorAll('[id^="Slide-"]'));
        if (slides.length < 2) return;

        // Build a map of media-id suffix → slide element
        const slideMap = new Map();
        slides.forEach((slide) => {
          const mediaId = slide.dataset.mediaId;
          if (mediaId) {
            // Extract the media ID number (after section prefix)
            const suffix = mediaId.replace(`${sectionId}-`, '');
            slideMap.set(suffix, slide);
          }
        });

        // Re-append slides in data-media-order sequence (only those present in DOM)
        let reordered = false;
        orderArr.forEach((mediaIdNum) => {
          const slide = slideMap.get(mediaIdNum);
          if (slide) {
            list.appendChild(slide);
            reordered = true;
          }
        });

        // Clear is-active from all slides (Swiper will handle active state)
        if (reordered) {
          slides.forEach((s) => s.classList.remove('is-active'));
        }
      }

      /**
       * Determine the correct initial slide index for the selected variant.
       * Uses the same data-media-order fallback as updateMedia() in product-info.js.
       */
      _getInitialSlideForVariant(list) {
        const productInfo = this.closest('product-info');
        if (!productInfo) return 0;

        const variantJson = productInfo.querySelector('variant-selects [data-selected-variant]');
        if (!variantJson) return 0;

        let variant;
        try {
          variant = JSON.parse(variantJson.innerHTML);
        } catch (e) {
          return 0;
        }
        if (!variant?.featured_media?.id) return 0;

        const sectionId = this.id.replace('MediaGallery-', '');
        const targetMediaId = `${sectionId}-${variant.featured_media.id}`;
        const slides = Array.from(list.querySelectorAll('[id^="Slide-"]'));

        // Try exact featured media first
        let targetIndex = slides.findIndex((s) => s.dataset.mediaId === targetMediaId);
        if (targetIndex >= 0) return targetIndex;

        // If hidden by hide_variants, walk forward in media order
        const mediaOrder = this.dataset.mediaOrder;
        if (!mediaOrder) return 0;

        const orderArr = mediaOrder.split(',');
        const featuredIdx = orderArr.indexOf(String(variant.featured_media.id));
        if (featuredIdx < 0) return 0;

        const galleryMediaIds = new Set(slides.map((s) => s.dataset.mediaId));
        for (let i = featuredIdx + 1; i < orderArr.length; i++) {
          const candidateId = `${sectionId}-${orderArr[i]}`;
          if (galleryMediaIds.has(candidateId)) {
            return slides.findIndex((s) => s.dataset.mediaId === candidateId);
          }
        }

        return 0;
      }

      /**
       * Navigate Swiper to a specific media slide by its data-media-id.
       * Uses slideTo() on the existing Swiper instance — no DOM changes.
       */
      navigateToMedia(mediaId) {
        if (!this.gallerySwiper) return false;

        const slides = this.gallerySwiper.slides;
        if (!slides || !slides.length) return false;

        const targetIndex = Array.from(slides).findIndex(
          (el) => el.dataset.mediaId === mediaId
        );
        if (targetIndex < 0) return false;

        this.gallerySwiper.slideTo(targetIndex);
        this.preventStickyHeader();

        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(activeThumbnail);
        }

        return true;
      }

      onSlideChanged(event) {
        if (!this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend, slidesChanged) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (this.gallerySwiper) {
          // Just navigate to the target slide — no DOM manipulation
          const slides = this.gallerySwiper.slides;
          const targetIndex = slides
            ? Array.from(slides).findIndex((el) => el.dataset.mediaId === mediaId)
            : -1;

          if (targetIndex >= 0) {
            this.gallerySwiper.slideTo(targetIndex);
          }
        } else if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.gallerySwiper && (!this.mql.matches || this.elements.thumbnails)) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
